import type { HoldSummary } from './dirtyRegistry';

/**
 * What to say when someone navigates away from work in progress.
 *
 * Until now the guards covered RELOADS and updates only. Moving between pages
 * inside the app destroyed unsaved edits silently — the component unmounted
 * and the typing went with it — while the feature's promise was that work is
 * protected. This closes that half.
 *
 * The two cases are not the same and must not offer the same choices:
 *
 *  unsaved  — the person typed something and has not saved it. Losing it is
 *             their call to make, so: Stay, or Discard and leave.
 *
 *  pending  — a request is in flight. There is no "discard": the server is
 *             already acting, and for a money action the ANSWER is the thing
 *             that must not be lost. Leaving cannot un-send it, so the only
 *             honest option is to wait.
 *
 * ⚠️ That "only option" holds for navigation the APP controls. A browser
 * unload — reload, tab close, cross-document Back — can only be warned
 * about; the person can still choose Leave and nothing can stop them. This
 * prevents an accidental departure, it does not guarantee anyone waits.
 */
export interface NavigationPlan {
  title: string;
  body: string;
  /** false for pending work: leaving cannot undo what is already in flight. */
  canDiscard: boolean;
  labels: string[];
}

const list = (labels: string[]): string => {
  const shown = labels.slice(0, 3);
  const more = labels.length > 3 ? ` and ${labels.length - 3} more` : '';
  return shown.length ? `${shown.join(', ')}${more}` : 'work in progress';
};

/** null = nothing to protect, so navigation must not be interrupted. */
export const planNavigation = (s: HoldSummary): NavigationPlan | null => {
  if (s.total === 0) return null;

  // ⚠️ Pending outranks unsaved, the same way it does for reloads. Losing the
  // result of something already sent is worse than losing something never
  // sent — and worse still when it is a payment.
  if (s.pending > 0) {
    return {
      title: 'Wait for this to finish',
      body: `Something is still being saved (${list(s.labels)}). Leaving now could lose the result, or repeat it. This usually takes a moment.`,
      canDiscard: false,
      labels: s.labels,
    };
  }

  return {
    title: 'Leave without saving?',
    body: `You have unsaved changes (${list(s.labels)}). They will be lost if you leave this page.`,
    canDiscard: true,
    labels: s.labels,
  };
};

/**
 * Whether a navigation is one worth interrupting at all.
 *
 * A re-render, or a change that keeps the person on the same screen, is not
 * someone leaving — interrupting those would make the dialog noise that gets
 * dismissed reflexively, which is how a real warning stops being read.
 */
export const isLeaving = (
  from: { pathname: string; search?: string },
  to: { pathname: string; search?: string },
): boolean =>
  from.pathname !== to.pathname || (from.search ?? '') !== (to.search ?? '');
